import {useSyncExternalStore} from 'use-sync-external-store/shim';

type OmniboxOpenListener = () => void;
type AvailabilityListener = () => void;

const listeners = new Set<OmniboxOpenListener>();
const availabilityListeners = new Set<AvailabilityListener>();

function notifyAvailabilityChanged(): void {
  availabilityListeners.forEach((listener) => {
    listener();
  });
}

/**
 * Register a callback to run when something requests the omnibox be opened, and return an
 * unsubscribe function. The Omnibox registers itself here while mounted so other surfaces (e.g. the
 * navbar search icon) can open it without holding a reference to its local open state.
 */
export function subscribeToOmniboxOpen(listener: OmniboxOpenListener): () => void {
  listeners.add(listener);
  notifyAvailabilityChanged();
  return () => {
    listeners.delete(listener);
    notifyAvailabilityChanged();
  };
}

/**
 * Request that the omnibox open. Returns true when a mounted omnibox handled the request, and false
 * when none is listening (e.g. the omnibox is disabled for this tenant/user) so callers can fall
 * back to alternate behavior.
 */
export function openOmnibox(): boolean {
  if (listeners.size === 0) {
    return false;
  }
  listeners.forEach((listener) => {
    listener();
  });
  return true;
}

/**
 * Whether a mounted omnibox is currently able to handle open requests. This is the same condition
 * {@link openOmnibox} uses to decide whether it handled the request, so surfaces that open the
 * omnibox (e.g. the navbar search icon) can rely on it to describe their behavior accurately —
 * announcing dialog semantics only when a click will actually open the omnibox.
 */
export function isOmniboxAvailable(): boolean {
  return listeners.size > 0;
}

/**
 * Subscribe to changes in omnibox availability (see {@link isOmniboxAvailable}), returning an
 * unsubscribe function. Availability changes when the omnibox mounts or unmounts, which can happen
 * after other surfaces have already rendered.
 */
export function subscribeToOmniboxAvailability(listener: AvailabilityListener): () => void {
  availabilityListeners.add(listener);
  return () => {
    availabilityListeners.delete(listener);
  };
}

/** React hook returning whether a mounted omnibox is available, re-rendering when that changes. */
export function useOmniboxAvailable(): boolean {
  return useSyncExternalStore(subscribeToOmniboxAvailability, isOmniboxAvailable);
}
